/**
 * 💳 AffiliateZahlungenPage - Auszahlungen & Rechnungen
 */

import React from 'react';
import { AffiliateLayout } from '@/components/affiliate/layout/AffiliateLayout';
import { StatusBadge } from '@/components/affiliate/badges/StatusBadge';
import { SkeletonStatCard, SkeletonTable } from '@/components/affiliate/skeletons';
import { EmptyStateNoPayouts } from '@/components/affiliate/empty/EmptyState';
import { useAffiliatePayouts } from '@/features/affiliate/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Wallet, 
  Download, 
  Calendar,
  FileText,
  CreditCard,
  AlertCircle,
  Clock
} from 'lucide-react';

// ============================================
// MAIN PAGE
// ============================================

export const AffiliateZahlungenPage: React.FC = () => {
  const {
    payouts,
    summary,
    isLoading,
    downloadInvoice,
    downloadAllInvoices,
    getStatusBadge,
  } = useAffiliatePayouts();

  // Loading State
  if (isLoading) {
    return (
      <AffiliateLayout
        pageTitle="Zahlungen"
        pageSubtitle="Auszahlungshistorie und Rechnungen"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map(i => <SkeletonStatCard key={i} />)}
        </div>
        <SkeletonTable rows={6} columns={6} />
      </AffiliateLayout>
    );
  }

  return (
    <AffiliateLayout
      pageTitle="Zahlungen"
      pageSubtitle="Auszahlungshistorie und Rechnungen"
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border-0 shadow-sm bg-gradient-to-br from-primary/5 to-primary/10">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/20 rounded-xl">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Nächste Auszahlung</p>
              <p className="text-2xl font-bold">€{summary.pendingPayout.toLocaleString('de-DE')}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Auszahlungsdatum</p>
              <p className="text-lg font-semibold">{summary.nextPayoutDate}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-100 rounded-xl">
              <Wallet className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Insgesamt ausgezahlt</p>
              <p className="text-2xl font-bold text-green-600">€{summary.lifetimePaid.toLocaleString('de-DE')}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gray-100 rounded-xl">
              <CreditCard className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Zahlungsmethode</p>
              <p className="text-sm font-semibold">{summary.paymentMethod}</p>
              <p className="text-xs text-gray-400 font-mono">{summary.iban}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="p-4 mb-6 bg-blue-50/80 border-blue-200">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-900 font-medium">Auszahlung am 05. des Monats</p>
            <p className="text-sm text-blue-700 mt-1">
              Alle freigegebenen Provisionen werden automatisch am 05. jedes Monats ausgezahlt. 
              Mindestauszahlungsbetrag: €50.
            </p>
          </div>
        </div>
      </Card>

      {/* Payout History */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">Auszahlungshistorie</CardTitle>
            <Button variant="outline" size="sm" onClick={() => downloadAllInvoices()}>
              <Download className="w-4 h-4 mr-2" />
              Alle Rechnungen
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-0">
          {payouts.length === 0 ? (
            <div className="py-8">
              <EmptyStateNoPayouts />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50/50">
                    <TableHead className="font-semibold">Datum</TableHead>
                    <TableHead className="font-semibold">Referenz</TableHead>
                    <TableHead className="font-semibold">Methode</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="text-right font-semibold">Betrag</TableHead>
                    <TableHead className="w-[80px]">Rechnung</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map((payout) => {
                    const status = getStatusBadge(payout.status);
                    return (
                      <TableRow key={payout.id}>
                        <TableCell className="text-gray-500">
                          {new Date(payout.date).toLocaleDateString('de-DE', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{payout.reference}</TableCell>
                        <TableCell>{payout.method}</TableCell>
                        <TableCell>
                          <StatusBadge variant={status.variant} label={status.label} size="sm" />
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          €{payout.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          {payout.invoiceAvailable ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => downloadInvoice(payout.id)}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          ) : (
                            <span className="text-gray-300 pl-2">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </AffiliateLayout>
  );
};

export default AffiliateZahlungenPage;
